/**
 * Language and direction.
 *
 * Nothing in the interface is written in a component - every word comes from
 * en.ts or ar.ts. Switching is instant: the html lang and dir attributes change,
 * the CSS logical properties mirror the whole layout, and the Arabic typeface
 * takes over.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en, type StringKey } from './en.js';
import { ar } from './ar.js';
import type { Lang } from '@pos/shared';

const DICTS: Record<Lang, Record<string, string>> = { en, ar };

interface I18n {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  setLang: (l: Lang) => void;
  /** t('checkout.outOfStock', { n: 3 }) */
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
  /** Show the field in the user's language, falling back to the other one. */
  pick: (x: { en?: string | null; ar?: string | null } | null | undefined) => string;
}

const Ctx = createContext<I18n | null>(null);

const STORAGE_KEY = 'pos.lang';

/**
 * English unless the person has chosen otherwise. Their choice is remembered on
 * that device, so a cashier who prefers Arabic sets it once.
 */
function initialLang(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'en' || saved === 'ar') return saved;
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang, dir]);

  const t = useCallback(
    (key: StringKey, vars?: Record<string, string | number>) => {
      const dict = DICTS[lang];
      let s = dict[key] ?? DICTS.en[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
      }
      return s;
    },
    [lang],
  );

  const pickFn = useCallback(
    (x: { en?: string | null; ar?: string | null } | null | undefined) => {
      if (!x) return '';
      const first = lang === 'ar' ? x.ar : x.en;
      const second = lang === 'ar' ? x.en : x.ar;
      return (first && first.trim()) || (second && second.trim()) || '';
    },
    [lang],
  );

  const value = useMemo<I18n>(
    () => ({ lang, dir, setLang: setLangState, t, pick: pickFn }),
    [lang, dir, t, pickFn],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const c = useContext(Ctx);
  if (!c) throw new Error('useI18n used outside the provider');
  return c;
}

/** Shorthand used in most components. */
export function useT() {
  return useI18n().t;
}

/**
 * Counting things out loud.
 *
 * English changes shape at one. Arabic changes at one, again at two, and again
 * past ten - "قطعة وحدة", "قطعتين", "٣ قطع", "١١ قطعة". Give this the four keys
 * and it picks the one that reads like a person wrote it.
 */
export function usePlural() {
  const { lang, t } = useI18n();
  return (
    n: number,
    keys: { one: StringKey; two: StringKey; few: StringKey; many?: StringKey },
  ): string => {
    if (n === 1) return t(keys.one);
    if (n === 2) return t(keys.two);
    if (lang === 'ar' && n > 10 && keys.many) return t(keys.many, { n });
    return t(keys.few, { n });
  };
}

/** A bilingual database row, e.g. { name_en, name_ar } -> the right one. */
export function useName() {
  const { pick } = useI18n();
  return (row: Record<string, unknown> | null | undefined, prefix = 'name'): string =>
    pick(row ? { en: row[`${prefix}_en`] as string, ar: row[`${prefix}_ar`] as string } : null);
}
